import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { endOfDay, format, startOfDay } from 'date-fns';
import { html, nothing } from 'lit';
import { guard } from 'lit/directives/guard.js';
import { IntercomEntity } from '../../entities/intercom.js';
import { IntercomDetailView } from './IntercomDetailView.js';

export const IntercomDashboard = stateful(() => {
  const [selectedIntercomID, setSelectedIntercomID] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [authorFilter, setAuthorFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [integrationsTest, setIntegrationstest] = useState<'yes' | 'no'>('no');
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Function to handle intercom row click
  const handleIntercomRowClick = (intercom: IntercomEntity) => {
    setSelectedIntercomID(intercom._id);
  };

  // Function to close the detail view
  const closeDetailView = () => {
    setSelectedIntercomID(null);
  };

  // Function to clear all filters
  const clearFilters = () => {
    setSearchText('');
    setAuthorFilter('');
    setStartDate('');
    setEndDate('');
    setIntegrationstest('no');
  };

  const intercomDataviewOptions = useMemo(() => {
    const filterAspects: any[] = [];

    // Add author filter
    if (authorFilter.trim()) {
      filterAspects.push(
        new StringAspect({
          fieldName: 'author',
          value: authorFilter,
          mode: 'contains',
        }),
      );
    }
    if (integrationsTest === 'yes') {
      filterAspects.push(
        new BooleanAspect({
          fieldName: 'isHistoricalOrIntegrationTest',
          value: true,
        }),
      );
    }

    // Add date range filter - if both dates are provided, use range mode
    if (startDate && endDate) {
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          firstValue: startOfDay(new Date(startDate)),
          secondValue: endOfDay(new Date(endDate)),
          mode: 'range',
        }),
      );
    } else if (startDate) {
      // Only start date - use after mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          firstValue: startOfDay(new Date(startDate)),
          mode: 'after',
        }),
      );
    } else if (endDate) {
      // Only end date - use before mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          secondValue: endOfDay(new Date(endDate)),
          mode: 'before',
        }),
      );
    }

    return [
      buildFullOptions<typeof IntercomEntity>({
        class: IntercomEntity,
        customColumns: [
          {
            fieldName: 'intercomId',
            columnName: 'Intercom Id',
            columnWidth: '150px',
          },
          {
            fieldName: 'isAgentAssist',
            columnName: 'Agent Assist Mode',
            columnWidth: '175px',
            renderItem: value => (value.isAgentAssist ? 'Yes' : 'No'),
          },
          {
            fieldName: 'lastAuthorType',
            columnName: 'Last Author Type',
            columnWidth: '150px',
          },
          { fieldName: 'message', columnName: 'Message' },
          {
            fieldName: 'createdAt',
            columnName: 'Date Created',
            columnWidth: '175px',
            renderItem: value => format(value.createdAt ?? new Date(), 'Pp'),
          },
          {
            fieldName: 'isCheckedBySupport',
            columnName: 'Checked by Support',
            columnWidth: '100px',
            renderItem: value =>
              value.isCheckedBySupport
                ? html`<span class="text-secondary">✅</span>`
                : html`<span class="text-error">❌</span>`,
          },
          {
            customID: '_options',
            columnName: '',
            columnWidth: '50px',
            renderItem: value => html`
              <button
                class="btn btn-ghost btn-sm btn-circle"
                @click=${() => handleIntercomRowClick(value)}
                title="View Details"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                  />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            `,
          },
        ],
        isSortable: false,
        emptyState: () => html`
          <div class="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div class="bg-base-200 p-4 rounded-full mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-12 h-12 text-secondary"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
                />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-secondary mb-2">No Intercom Messages</h3>
            <p class="text-base-content opacity-70 max-w-md">
              There are currently no intercom messages to display. New messages will appear here when users interact
              with the intercom system.
            </p>
          </div>
        `,
        view: new View({
          filter: new AdornisFilter({
            searchFields: ['message', 'botResponse', 'comment', 'comments', 'intercomId'],
            search: searchText,
            aspects: filterAspects,
          }),
        }),
      }),
    ];
  }, [searchText, authorFilter, startDate, endDate, integrationsTest]);

  return html`
    <div class="flex gap-4 h-full">
      <div
        class="flex-1 card bg-base-100 shadow-xl border border-base-300 overflow-auto ${selectedIntercomID
          ? 'w-2/3'
          : 'w-full'}"
      >
        <div class="card-body">
          <div class="flex justify-between items-center mb-2 sticky top-0 bg-base-100 z-10 py-4">
            <h2 class="card-title text-2xl font-bold text-secondary">Intercom Data</h2>

            <div class="flex items-center gap-2">
              <button class="btn btn-sm btn-outline btn-secondary" @click=${() => setShowFilters(!showFilters)}>
                ${showFilters ? 'Hide Filters' : 'Show Filters'}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-4 h-4 ml-1"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"
                  />
                </svg>
              </button>
            </div>
          </div>

          ${showFilters
            ? html`
                <div class="bg-base-200 p-4 rounded-lg mb-4">
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <!-- Text Search -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Text Search</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Search in messages and responses..."
                        class="input input-bordered input-sm w-full"
                        .value=${searchText}
                        @input=${(e: InputEvent) => setSearchText((e.target as HTMLInputElement).value)}
                      />
                    </div>

                    <!-- Author Filter -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Author</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Filter by author..."
                        class="input input-bordered input-sm w-full"
                        .value=${authorFilter}
                        @input=${(e: InputEvent) => setAuthorFilter((e.target as HTMLInputElement).value)}
                      />
                    </div>

                    <!-- Integrationstest -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Integrationstest</span>
                      </label>
                      <select
                        class="select select-sm select-bordered w-full"
                        .value=${integrationsTest}
                        @change=${(e: InputEvent) =>
                          setIntegrationstest((e.target as HTMLSelectElement).value as 'yes' | 'no')}
                      >
                        <option value="yes">yes</option>
                        <option value="no">no</option>
                      </select>
                    </div>

                    <!-- Date Range - Combined in one container to keep them together -->
                    <div class="form-control col-span-1 md:col-span-2 lg:col-span-1">
                      <label class="label">
                        <span class="label-text font-medium">Date Range</span>
                      </label>
                      <div class="flex gap-2">
                        <div class="flex-1">
                          <input
                            type="date"
                            placeholder="Start date"
                            class="input input-bordered input-sm w-full"
                            .value=${startDate}
                            @input=${(e: InputEvent) => setStartDate((e.target as HTMLInputElement).value)}
                          />
                        </div>
                        <div class="flex-1">
                          <input
                            type="date"
                            placeholder="End date"
                            class="input input-bordered input-sm w-full"
                            .value=${endDate}
                            @input=${(e: InputEvent) => setEndDate((e.target as HTMLInputElement).value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Filter Actions -->
                  <div class="flex justify-end mt-4">
                    <button class="btn btn-sm btn-outline btn-error mr-2" @click=${clearFilters}>Clear Filters</button>
                  </div>
                </div>
              `
            : nothing}

          <div class="overflow-x-auto">
            ${guard([intercomDataviewOptions], () =>
              DataTable({
                options: intercomDataviewOptions.dataViewOptions,
              }),
            )}
          </div>
        </div>
      </div>

      <!-- Intercom Detail view panel -->
      ${selectedIntercomID
        ? html`
            <div class="w-1/3">${IntercomDetailView({ intercomID: selectedIntercomID, onClose: closeDetailView })}</div>
          `
        : nothing}
    </div>
  `;
});
